import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Search, Edit, Trash2, Users } from "lucide-react";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

type Partner = Tables<"partners">;
type PartnerInsert = TablesInsert<"partners">;
type PartnerUpdate = TablesUpdate<"partners">;
type Profile = Tables<"profiles">;

export default function Partners() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUsersDialogOpen, setIsUsersDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);
  const [formData, setFormData] = useState<Partial<PartnerInsert>>({
    type: "workshop",
    name: "",
    business_id: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
  });

  
  const queryClient = useQueryClient();

  // Fetch partners
  const { data: partners = [], isLoading } = useQuery({
    queryKey: ["partners", searchQuery],
    queryFn: async () => {
      let query = supabase
        .from("partners")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (searchQuery) {
        query = query.or(
          `name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,phone.ilike.%${searchQuery}%,city.ilike.%${searchQuery}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Partner[];
    },
  });

  // Fetch partner users
  const { data: partnerUsers = [] } = useQuery({
    queryKey: ["partner-users", selectedPartner?.id],
    queryFn: async () => {
      if (!selectedPartner) return [];
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("partner_id", selectedPartner.id)
        .is("deleted_at", null);

      if (error) throw error;
      return data as Profile[];
    },
    enabled: !!selectedPartner && isUsersDialogOpen,
  });

  // Create partner mutation
  const createMutation = useMutation({
    mutationFn: async (data: PartnerInsert) => {
      const { error } = await supabase.from("partners").insert(data);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast.success("Kumppani lisätty", {
        description: "Uusi kumppani on lisätty onnistuneesti.",
      });
      handleCloseDialog();
    },
    onError: () => {
      toast.error("Virhe", {
        description: "Kumppanin lisääminen epäonnistui.",
      });
    },
  });

  // Update partner mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: PartnerUpdate;
    }) => {
      const { error } = await supabase
        .from("partners")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast.success("Kumppani päivitetty", {
        description: "Kumppanin tiedot on päivitetty onnistuneesti.",
      });
      handleCloseDialog();
    },
    onError: () => {
      toast.error("Virhe", {
        description: "Kumppanin päivittäminen epäonnistui.",
      });
    },
  });

  // Delete partner mutation (soft delete)
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("partners")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast.success("Kumppani poistettu", {
        description: "Kumppani on poistettu onnistuneesti.",
      });
      setIsDeleteDialogOpen(false);
      setPartnerToDelete(null);
    },
    onError: () => {
      toast.error("Virhe", {
        description: "Kumppanin poistaminen epäonnistui.",
      });
    },
  });

  const handleOpenDialog = (partner?: Partner) => {
    if (partner) {
      setSelectedPartner(partner);
      setFormData({
        type: partner.type,
        name: partner.name,
        business_id: partner.business_id || "",
        email: partner.email || "",
        phone: partner.phone || "",
        address: partner.address || "",
        postal_code: partner.postal_code || "",
        city: partner.city || "",
      });
    } else {
      setSelectedPartner(null);
      setFormData({
        type: "workshop",
        name: "",
        business_id: "",
        email: "",
        phone: "",
        address: "",
        postal_code: "",
        city: "",
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedPartner(null);
    setFormData({
      type: "workshop",
      name: "",
      business_id: "",
      email: "",
      phone: "",
      address: "",
      postal_code: "",
      city: "",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.type) {
      toast.error("Virhe", {
        description: "Täytä vaaditut kentät.",
      });
      return;
    }

    if (selectedPartner) {
      updateMutation.mutate({
        id: selectedPartner.id,
        data: formData as PartnerUpdate,
      });
    } else {
      createMutation.mutate(formData as PartnerInsert);
    }
  };

  const handleDelete = (partner: Partner) => {
    setPartnerToDelete(partner);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (partnerToDelete) {
      deleteMutation.mutate(partnerToDelete.id);
    }
  };

  const handleViewUsers = (partner: Partner) => {
    setSelectedPartner(partner);
    setIsUsersDialogOpen(true);
  };

  const getPartnerTypeLabel = (type: string) => {
    switch (type) {
      case "workshop":
        return "Työpaja";
      case "installer":
        return "Asentaja";
      default:
        return type;
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Kumppanit</h1>
            <p className="text-muted-foreground">
              Hallitse kumppaneita ja yhteystietoja
            </p>
          </div>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            Lisää kumppani
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Hae kumppaneita..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nimi</TableHead>
                <TableHead>Tyyppi</TableHead>
                <TableHead>Y-tunnus</TableHead>
                <TableHead>Puhelin</TableHead>
                <TableHead>Sähköposti</TableHead>
                <TableHead>Kaupunki</TableHead>
                <TableHead className="text-right">Toiminnot</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center">
                    Ladataan...
                  </TableCell>
                </TableRow>
              ) : partners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center">
                    Ei kumppaneita
                  </TableCell>
                </TableRow>
              ) : (
                partners.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell className="font-medium">{partner.name}</TableCell>
                    <TableCell>{getPartnerTypeLabel(partner.type)}</TableCell>
                    <TableCell>{partner.business_id || "-"}</TableCell>
                    <TableCell>{partner.phone || "-"}</TableCell>
                    <TableCell>{partner.email || "-"}</TableCell>
                    <TableCell>{partner.city || "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewUsers(partner)}
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(partner)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(partner)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) { handleCloseDialog(); createMutation.reset(); updateMutation.reset(); } else { setIsDialogOpen(true); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedPartner ? "Muokkaa kumppania" : "Lisää kumppani"}
            </DialogTitle>
            <DialogDescription>
              Täytä kumppanin tiedot. Pakolliset kentät on merkitty tähdellä.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Nimi <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">
                  Tyyppi <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, type: value as "workshop" | "installer" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workshop">Työpaja</SelectItem>
                    <SelectItem value="installer">Asentaja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="business_id">Y-tunnus</Label>
                <Input
                  id="business_id"
                  value={formData.business_id}
                  onChange={(e) =>
                    setFormData({ ...formData, business_id: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Puhelin</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Sähköposti</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Osoite</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postal_code">Postinumero</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) =>
                    setFormData({ ...formData, postal_code: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Kaupunki</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
              >
                Peruuta
              </Button>
              <Button type="submit">
                {selectedPartner ? "Tallenna" : "Lisää"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Haluatko varmasti poistaa?</AlertDialogTitle>
            <AlertDialogDescription>
              Tämä poistaa kumppanin {partnerToDelete?.name}. Toimintoa ei voi
              perua.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Peruuta</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Poista
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Linked Users Dialog */}
      <Dialog open={isUsersDialogOpen} onOpenChange={setIsUsersDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Linkitetyt käyttäjät - {selectedPartner?.name}</DialogTitle>
            <DialogDescription>
              Käyttäjät, jotka on linkitetty tähän kumppaniin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {partnerUsers.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Ei linkitettyjä käyttäjiä
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nimi</TableHead>
                    <TableHead>Puhelin</TableHead>
                    <TableHead>Luotu</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partnerUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell>{user.phone || "-"}</TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString("fi-FI")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
